import { apiFetch, showAppState } from "./app-state.jsx";
import React from "react";
import "./inquiry.css";
import { SiteHeader as Header, SiteFooter as Footer } from "./chrome.jsx";
import {
  FaArrowRight,
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaLocationDot,
  FaPhone,
} from "react-icons/fa6";

function getApiUrl() {
  return process.env.REACT_APP_API_URL?.replace(/\/+$/, "");
}

function InquiryForm() {
  const initialValues = {
    name: '',
    email: '',
    phone: '',
    type: '',
    message: ''
  };
  const [values, setValues] = React.useState(initialValues);
  const [errors, setErrors] = React.useState({});
  const [status, setStatus] = React.useState('');
  const [statusKind, setStatusKind] = React.useState('error');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  function validate(formValues) {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@gmail\.com$/i;
    const phonePattern = /^\d{10}$/;

    if (!formValues.name.trim()) {
      nextErrors.name = 'Full name is required.';
    } else if (formValues.name.trim().length < 2) {
      nextErrors.name = 'Enter at least 2 characters.';
    }

    if (!formValues.email.trim()) {
      nextErrors.email = 'Email address is required.';
    } else if (!emailPattern.test(formValues.email.trim())) {
      nextErrors.email = 'Enter a valid Gmail address.';
    }

    if (!formValues.phone.trim()) {
      nextErrors.phone = 'Phone number is required.';
    } else if (!phonePattern.test(formValues.phone.trim())) {
      nextErrors.phone = 'Enter exactly 10 digits.';
    }

    if (!formValues.type) {
      nextErrors.type = 'Please select an inquiry type.';
    }

    if (!formValues.message.trim()) {
      nextErrors.message = 'Message is required.';
    } else if (formValues.message.trim().length < 10) {
      nextErrors.message = 'Message must be at least 10 characters.';
    }

    return nextErrors;
  }

  function handleChange(event) {
    const { name, value } = event.target;
    const nextValue = name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value;
    const nextValues = { ...values, [name]: nextValue };

    setValues(nextValues);
    setStatus('');

    if (errors[name]) {
      setErrors(validate(nextValues));
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatusKind('error');

    const nextErrors = validate(values);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showAppState("form-validation", { errors: nextErrors });
      setStatus('');
      return;
    }

    setIsSubmitting(true);
    setStatus('');

    try {
      const apiUrl = getApiUrl();

      if (!apiUrl) {
        setStatus('API URL is not configured.');
        return;
      }

      const response = await apiFetch(`${apiUrl}/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim() || undefined,
          type: values.type,
          message: values.message.trim()
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setStatus(data.message || 'Could not submit inquiry. Please try again.');
        return;
      }

      showAppState("success", { message: data.message || "Inquiry submitted successfully." });
      setStatusKind('success');
      setStatus(data.message || 'Inquiry submitted successfully.');
      setValues(initialValues);
    } catch (error) {
      console.error('INQUIRY ERROR:', error);
      setStatus('Cannot connect to the backend server.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function fieldError(name) {
    return errors[name] ? <span className="field-error">{errors[name]}</span> : null;
  }

  return (
    <form className="message-card" onSubmit={handleSubmit} noValidate>
      <h2>Send Us a Message</h2>
      <div className="form-grid">
        <label>
          <span className="label-text">Full Name <span className="required-star">*</span></span>
          <input
            className={errors.name ? 'has-error' : ''}
            type="text"
            name="name"
            value={values.name}
            placeholder="Enter your name"
            aria-invalid={Boolean(errors.name)}
            onChange={handleChange}
          />
          {fieldError('name')}
        </label>
        <label>
          <span className="label-text">Email Address <span className="required-star">*</span></span>
          <input
            className={errors.email ? 'has-error' : ''}
            type="email"
            name="email"
            value={values.email}
            placeholder="Enter your email"
            aria-invalid={Boolean(errors.email)}
            onChange={handleChange}
          />
          {fieldError('email')}
        </label>
        <label>
          <span className="label-text">Phone Number <span className="required-star">*</span></span>
          <input
            className={errors.phone ? 'has-error' : ''}
            type="tel"
            name="phone"
            value={values.phone}
            placeholder="Enter your number"
            inputMode="numeric"
            maxLength={10}
            pattern="[0-9]{10}"
            aria-invalid={Boolean(errors.phone)}
            onChange={handleChange}
          />
          {fieldError('phone')}
        </label>
        <label>
          <span className="label-text">Inquiry Type <span className="required-star">*</span></span>
          <select
            className={errors.type ? 'has-error' : ''}
            name="type"
            value={values.type}
            aria-invalid={Boolean(errors.type)}
            onChange={handleChange}
          >
            <option value="" disabled>Select type</option>
            <option>Project</option>
            {/* <option>Careers</option> */}
            <option>Partnership</option>
          </select>
          {fieldError('type')}
        </label>
      </div>
      <label>
        <span className="label-text">Message <span className="required-star">*</span></span>
        <textarea
          className={errors.message ? 'has-error' : ''}
          name="message"
          value={values.message}
          placeholder="Tell us about your inquiry..."
          aria-invalid={Boolean(errors.message)}
          onChange={handleChange}
        />
        {fieldError('message')}
      </label>
      {status && <p className={`form-feedback form-${statusKind}`} role={statusKind === 'error' ? 'alert' : 'status'}>{status}</p>}
      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit Inquiry'} <FaArrowRight aria-hidden="true" />
      </button>
    </form>
  );
}

function ContactDetails() {
  return (
    <aside className="contact-card">
      <h2>Contact Details</h2>
      <ul>
        <li><span><FaPhone aria-hidden="true" /></span><a href="tel:+919876543210">+91 98765 43210</a></li>
        <li><span><FaEnvelope aria-hidden="true" /></span><a href="mailto:info@halleftechnologies.com">info@halleftechnologies.com</a></li>
        <li><span><FaLocationDot aria-hidden="true" /></span><p>Bengaluru, India</p></li>
      </ul>
      <div className="follow">
        <p>Follow Us</p>
        <div>
          <a href="https://www.linkedin.com/in/nex-move-600063276/" target="_blank" rel="noreferrer" aria-label="LinkedIn" title="LinkedIn"><FaLinkedinIn aria-hidden="true" /></a>
          <a href="https://www.instagram.com/nexmove2024?igsh=NWczeWliN3R3N3Ji" target="_blank" rel="noreferrer" aria-label="Instagram" title="Instagram"><FaInstagram aria-hidden="true" /></a>
          <a href="https://www.facebook.com/Nexmove2019/" target="_blank" rel="noreferrer" aria-label="Facebook" title="Facebook"><FaFacebookF aria-hidden="true" /></a>
        </div>
      </div>
    </aside>
  );
}

function Hero() {
  return (
    <main>
      {/* <div className="breadcrumb"><a href="/">Home</a><span>›</span><span>Inquiry</span></div>m,kkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkk */}
      <section className="inquiry-hero">
        <div className="hero-copy">
          <p className="kicker" sty
          >Get in Touch</p>
          <h1>Have an Inquiry?<br /><span>We’re Here to Help.</span></h1>
        </div>
        <div className="contact-visual" aria-hidden="true">
          <div className="visual-card main-card">
            <span className="mail-shape" />
            <strong>Let’s talk</strong>
            <p>Share your idea, question or requirement.</p>
          </div>
          <div className="visual-card reply-card">
            <small>Response</small>
            <strong>24h</strong>
          </div>
          <div className="visual-card detail-card">
            <span />
            <span />
            <span />
          </div>
        </div>
      </section>
      <section className="inquiry-grid">
        <InquiryForm />
        <ContactDetails />
      </section>
    </main>
  );
}

function App() {
  return <><Header /><Hero /><Footer /></>;
}

export default App;
